/* eslint-disable no-nested-ternary */
import React, { useMemo } from 'react'
import moment from 'moment'
import ms from 'ms'
import useCountdown from '../../../hooks/useCountdown'
import Text from '../text'
import Icon from '../icon'
import { formatPeriodTime } from '../../../utils/countdown'
import AnimatedProgressBar from '../animatedProgressBar'
import classnames from 'classnames/bind'
import { ColorNames } from '../../../constants/colorVars'
import { AutomationTarget } from '../../../../src/types'

import styles from './styles.scss'
const cx = classnames.bind(styles)

const COUNTDOWN_UPDATE_FREQUENCIES = {
  EVERY_SECOND: ms('1s'),
  EVERY_THIRTY_SECONDS: ms('1m') / 2,
  EVERY_FIVE_MINUTES: ms('1m') * 5,
}

type RestrictedColorNames =
  | ColorNames.Gray
  | ColorNames.Info
  | ColorNames.Success
  | ColorNames.AdcLightGreen
  | ColorNames.Caution
  | ColorNames.Error
  | ColorNames.Purple

export interface PeriodIndicatorProps extends AutomationTarget {
  className?: string
  style?: React.CSSProperties
  color?: RestrictedColorNames
  iconName: string
  title: string
  startDate?: Date
  endDate?: Date
  titleOnly?: boolean
  fluid?: boolean
  subTitle?: string
}

const PeriodIndicator: React.FC<PeriodIndicatorProps> = ({
  className,
  elmId = 'progress_indicator',
  color: colorName = ColorNames.Info,
  iconName,
  style,
  title,
  startDate,
  endDate,
  titleOnly = false,
  fluid,
  subTitle = 'This event will end in: ',
}) => {
  const now = moment()
  const start = startDate ? moment(startDate) : null
  const end = endDate ? moment(endDate) : null
  const duration = end ? moment.duration(end.diff(now)) : null
  const datesAreInvalid =
    !duration ||
    isNaN(duration.asSeconds()) ||
    moment(startDate).isAfter(endDate)

  const percent = datesAreInvalid
    ? 0
    : moment().isAfter(endDate)
    ? 100
    : Math.min(
        100,
        (moment().diff(startDate, 'seconds') /
          moment(endDate).diff(startDate, 'seconds')) *
          100
      )

  // @ts-ignore needs types
  const { secondsRemaining } = useCountdown({
    // @ts-ignore needs types
    endTime: moment(endDate),
    updateFrequency:
      duration && duration.asMinutes() < 5
        ? COUNTDOWN_UPDATE_FREQUENCIES.EVERY_SECOND
        : duration && duration.asMinutes() < 60
        ? COUNTDOWN_UPDATE_FREQUENCIES.EVERY_THIRTY_SECONDS
        : COUNTDOWN_UPDATE_FREQUENCIES.EVERY_FIVE_MINUTES,
  })

  const subtitle = useMemo(() => {
    if (!startDate || !endDate || datesAreInvalid) {
      return ''
    }
    if (start && now.isBefore(start)) {
      return (
        <span>
          This event will start on <strong>{start.format('MM/DD')}</strong>
        </span>
      )
    } else if (end && now.isAfter(end)) {
      return 'This event has ended'
    } else if (end) {
      return (
        <span>
          {subTitle}
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
          <strong>{formatPeriodTime(secondsRemaining)}</strong>
        </span>
      )
    }
    return ''
  }, [startDate, endDate, datesAreInvalid, start, now, end, secondsRemaining])

  return (
    <div
      className={cx('period-indicator', { fluid }, className)}
      style={style}
      data-elm-id={elmId}
    >
      <div className={cx('title')}>
        {iconName && (
          <Icon
            name={iconName}
            color={colorName}
            size='large'
            iconStyle='solid'
            className={cx('icon')}
          />
        )}
        {title && (
          <Text.Header level={2} color={colorName} className={cx('title-text')}>
            {title}
          </Text.Header>
        )}
      </div>
      {!titleOnly && (
        <div className={cx('lower-part')}>
          <Text size='tiny' color={ColorNames.Gray} className={cx('subtitle')}>
            {subtitle}
          </Text>
          <AnimatedProgressBar color={colorName} percent={percent} rounded />
        </div>
      )}
    </div>
  )
}

export default PeriodIndicator
